'use client';

import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';

import { ArrowLeftSmall, Close, Mug, Search } from '@zoomit/icons';
import { Flex, IconButton, Link } from '@zoomit/primitives';
import { IconRenderer, onMenuClick, OriginURL, ToggleThemeMobile } from '@zoomit/web-kit';
import { cssVar, Input, theme } from '@zoomit/zoomit-ds';

import { canDisplayMenuItem } from '../../../helpers/menu/canDisplayMenuItem';
import { useWebKitCustomization } from '../../../hooks/useWebKitCustomization';
import MenuAvatarMobile from './DefaultMenuMobileAvatar';
import { useDefaultMenuContext } from './DefaultMenuModuleProvider';

type Props = {
  onCloseMenu(): void;
};

const DefaultMenuHomeRouteMobile = (props: Props) => {
  const { onCloseMenu } = props;
  const basePath = useWebKitCustomization((settings) => settings.basePath);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { menuItems } = useDefaultMenuContext();
  const tenantPersianTitle = useWebKitCustomization((state) => state.tenantPersianTitle);
  const tenantLogoRenderer = useWebKitCustomization((state) => state.tenantLogoRenderer);

  const urlBasePath = basePath ? OriginURL : '';

  return (
    <>
      <header
        className={
          'bg-background2 border-b-grey6 sticky top-0 flex w-full items-center justify-between border-b px-5 py-[13px]'
        }
      >
        <IconButton onClick={onCloseMenu}>
          <Close fill={cssVar(theme.grey3)} size="xl" />
        </IconButton>

        <Link href={`${OriginURL}/${basePath}`} style={{ display: 'flex' }}>
          {tenantLogoRenderer({ fill: cssVar(theme.text) })}
        </Link>

        <MenuAvatarMobile Logo={() => tenantLogoRenderer({ fill: cssVar(theme.text) })} />
      </header>

      <div className={'px-6 py-4'}>
        <Input
          style={{ background: 'none' }}
          rightNode={<Search />}
          variant="rounded"
          placeholder="جستجو کنید"
          onClick={() => navigate('/search')}
        />
        <Flex flexDirection="column" className={'group'} gap={0.8} spacing={{ mt: 'x4' }}>
          {isAuthenticated && (
            <Link
              href={
                user?.profile?.isAuthor
                  ? `${urlBasePath}/author/${user?.profile.sub}`
                  : `${urlBasePath}/profile/${user?.profile.sub}`
              }
              onClick={() => {
                onMenuClick({ menu: `${tenantPersianTitle} من`, device: 'mobile' });
              }}
              style={{ all: 'unset' }}
              className="text-body text-text group-hover:text-primary1 flex items-center gap-2 leading-7"
            >
              <Mug size="sm" className={'fill-grey1 group-hover:fill-primary1 h-3 w-3'} />
              {tenantPersianTitle} من
            </Link>
          )}
        </Flex>

        <div className={'bg-grey6 mx-0 my-4 h-px w-full'} />

        <Flex flexDirection="column" gap={0.8} spacing={{ mt: 'x4' }}>
          {menuItems.map((item, index) => {
            const handleNavigate = () => navigate(`/submenu/${item.title}`, { state: item });

            return item.children && item.children.length > 0 ? (
              <Flex
                className={'group'}
                alignItems="center"
                justifyContent="space-between"
                onClick={handleNavigate}
              >
                <button
                  style={{ all: 'unset' }}
                  className="text-body text-text group-hover:text-primary1 flex items-center gap-2 leading-7"
                >
                  {canDisplayMenuItem.renderIcon(item.displayType) && item.icon && (
                    <IconRenderer
                      className={'fill-grey1 group-hover:fill-primary1 h-3 w-3'}
                      icon={item.icon.content}
                    />
                  )}
                  {item.title}
                </button>
                <ArrowLeftSmall size="sm" />
              </Flex>
            ) : (
              item.url && (
                <Link
                  onClick={() => {
                    onMenuClick({ menu: item.title, device: 'mobile' });
                  }}
                  href={item.url}
                  key={`${item.title}-${index}`}
                  style={{ all: 'unset' }}
                  className="text-body text-text group-hover:text-primary1 flex items-center gap-2 leading-7"
                >
                  {canDisplayMenuItem.renderIcon(item.displayType) && item.icon && (
                    <IconRenderer
                      className={'fill-grey1 group-hover:fill-primary1 h-3 w-3'}
                      icon={item.icon.content}
                    />
                  )}
                  {item.title}
                </Link>
              )
            );
          })}
        </Flex>
        <div className={'bg-grey6 mx-0 my-4 h-px w-full'} />
        <ToggleThemeMobile />
      </div>
    </>
  );
};

export default DefaultMenuHomeRouteMobile;
